import React, { useEffect, useRef, useState } from 'react';
import HanziWriter from 'hanzi-writer';

/**
 * Check if a character is likely a Chinese character that HanziWriter can handle
 * HanziWriter works with CJK Unified Ideographs (U+4E00 to U+9FFF)
 */
const isValidHanziCharacter = (char) => {
  const code = char.charCodeAt(0);
  // CJK Unified Ideographs range
  return code >= 0x4E00 && code <= 0x9FFF;
};

/**
 * StrokeAnimation Component
 *
 * Displays animated stroke order for Chinese characters
 * Uses hanzi-writer library for authentic stroke animations
 *
 * Props:
 * @param {string} character - The Chinese character(s) to animate
 * @param {boolean} autoAnimate - Start animation automatically on mount (default: false)
 * @param {number} size - Width/height of the animation container (default: 200)
 * @param {string} className - Additional CSS classes
 *
 * Features:
 * - Automatic stroke order animation
 * - Click to replay
 * - Displays character outline
 * - Responsive sizing
 *
 * PHASE 2 TODO:
 * - Add practice mode where user can draw the character
 * - Add quiz mode to test stroke order knowledge
 * - Support for multi-character animations in sequence
 */
function StrokeAnimation({
  character,
  autoAnimate = false,
  size = 200,
  className = '',
  strokeColor = null,  // Custom stroke color (overrides default)
  outlineColor = null, // Custom outline color (overrides default)
  interactive = true,  // false → render static glyph only (no click-to-animate)
}) {
  const writerContainerRef = useRef(null);
  const writerInstancesRef = useRef([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Initialize HanziWriter
  useEffect(() => {
    if (!character || !writerContainerRef.current) return;

    // Clear previous instances
    writerContainerRef.current.innerHTML = '';
    writerInstancesRef.current = [];
    setLoadError(false);

    // Split character string into individual characters and filter to valid hanzi only
    const allChars = character.split('');
    const characters = allChars.filter(isValidHanziCharacter);

    // If no valid characters, show static text instead
    if (characters.length === 0) {
      setLoadError(true);
      setHasLoaded(true);
      return;
    }

    // Calculate responsive size based on character count
    let charSize = size;
    const charCount = characters.length;

    // Scale down for longer text to fit in viewport
    if (charCount >= 10) {
      charSize = Math.min(size, 120);
    } else if (charCount >= 7) {
      charSize = Math.min(size, 150);
    } else if (charCount >= 5) {
      charSize = Math.min(size, 180);
    } else if (charCount >= 3) {
      charSize = Math.min(size, 220);
    }

    const padding = charCount > 4 ? 5 : 10;
    const totalWidth = characters.length * charSize + (characters.length - 1) * padding;

    // Set container width
    writerContainerRef.current.style.width = `${totalWidth}px`;
    writerContainerRef.current.style.height = `${charSize}px`;

    // Create a writer for each character
    characters.forEach((char, index) => {
      // Create SVG container for this character
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', charSize);
      svg.setAttribute('height', charSize);
      svg.style.position = 'absolute';
      svg.style.left = `${index * (charSize + padding)}px`;
      svg.style.top = '0';

      writerContainerRef.current.appendChild(svg);

      try {
        // Default vibrant colors for each character (Classic theme)
        const defaultCharacterColors = [
          '#2D5245', // Forest green
          '#5C8F4A', // Bright green
          '#4A7566', // Sage green
          '#6CBF7A', // Light sage
          '#D4A556', // Golden
        ];

        // Use different color for each character position (if no custom color)
        const colorIndex = index % defaultCharacterColors.length;
        const finalStrokeColor = strokeColor || defaultCharacterColors[colorIndex];
        const finalOutlineColor = outlineColor || '#E8E3D8';

        const writer = HanziWriter.create(svg, char, {
          width: charSize,
          height: charSize,
          padding: 5,
          showOutline: true,
          strokeAnimationSpeed: 1.2,
          delayBetweenStrokes: 150,
          strokeColor: finalStrokeColor,
          outlineColor: finalOutlineColor,
          drawingColor: finalStrokeColor,
          radicalColor: strokeColor || '#C87533', // Use custom color or amber
          strokeFadeDuration: 400,
          onLoadCharDataError: (reason, tries) => {
            console.warn(`Failed to load char data for ${char}:`, reason);
            // Don't crash - just skip this character
          },
        });

        writerInstancesRef.current.push(writer);

        // Auto-animate if requested (only after all characters loaded)
        if (index === characters.length - 1) {
          setHasLoaded(true);
        }
      } catch (error) {
        console.warn(`Could not load character: ${char}`, error);
      }
    });

    // Cleanup
    return () => {
      writerInstancesRef.current = [];
      const container = writerContainerRef.current;
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [character, size, strokeColor, outlineColor]);

  // Auto-animate when loaded
  useEffect(() => {
    if (hasLoaded && autoAnimate && writerInstancesRef.current.length > 0) {
      animateStrokes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded, autoAnimate]);

  /**
   * Animate all character strokes in sequence with different colors
   */
  const animateStrokes = async () => {
    if (isAnimating || writerInstancesRef.current.length === 0) return;

    setIsAnimating(true);

    // If a custom strokeColor was passed, use it for every stroke.
    // Otherwise cycle through the default rainbow palette.
    const strokeColors = strokeColor
      ? [strokeColor]
      : [
          '#2D5245', // Forest green
          '#5C8F4A', // Bright green
          '#4A7566', // Sage green
          '#6CBF7A', // Light sage
          '#D4A556', // Golden
          '#C87533', // Amber
          '#4A5C7C', // Indigo
          '#7BAF68', // Lime green
          '#3A4A5F', // Deep blue
          '#A0623B', // Brown
        ];

    // Animate each character in sequence
    for (const writer of writerInstancesRef.current) {
      try {
        // Get character data safely
        const characterData = writer._character;
        if (!characterData || !characterData.strokes) {
          // Fallback to regular animation if stroke data unavailable
          await new Promise((resolve) => {
            writer.animateCharacter({ onComplete: resolve });
          });
          continue;
        }

        const numStrokes = characterData.strokes.length;

        // Show the outline first so strokes fill in from empty state
        writer.hideCharacter();
        writer.showOutline();

        // Animate each stroke with a different color
        for (let i = 0; i < numStrokes; i++) {
          const strokeColor = strokeColors[i % strokeColors.length];

          await new Promise((resolve) => {
            writer.animateStroke(i, {
              strokeColor: strokeColor,
              onComplete: resolve
            });
          });
        }
      } catch (error) {
        console.warn('Error animating strokes with colors:', error);
        // Fallback to regular animation
        await new Promise((resolve) => {
          writer.animateCharacter({ onComplete: resolve });
        });
      }
    }

    setIsAnimating(false);
  };

  /**
   * Handle click to replay animation
   */
  const handleClick = () => {
    if (!isAnimating) {
      animateStrokes();
    }
  };

  if (!character) return null;

  // If no valid hanzi characters, show static text
  if (loadError) {
    return (
      <div className={`stroke-animation ${className} stroke-animation-fallback`}>
        <div className="stroke-animation-static">
          {character}
        </div>
      </div>
    );
  }

  // Static (non-interactive) render — no click handlers, no pointer cursor.
  // Used on the flashcard back where we want the glyph look but not the
  // tap-to-animate behaviour.
  if (!interactive) {
    return (
      <div className={`stroke-animation ${className}`}>
        <div
          ref={writerContainerRef}
          className="stroke-animation-container"
          style={{ position: 'relative', display: 'inline-block' }}
        />
      </div>
    );
  }

  return (
    <div
      className={`stroke-animation ${className} ${isAnimating ? 'animating' : ''}`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent card flip
        handleClick();
      }}
      role="button"
      tabIndex={0}
      aria-label="Click to play stroke animation"
      title="Click to play animation"
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          handleClick();
        }
      }}
    >
      <div
        ref={writerContainerRef}
        className="stroke-animation-container"
        style={{
          position: 'relative',
          display: 'inline-block',
          cursor: isAnimating ? 'wait' : 'pointer'
        }}
      />
    </div>
  );
}

export default StrokeAnimation;
